// Turning what people type ("2 lbs Bananas!", "milk x2") into a name, a quantity and a key
// that's the same for "Banana", "bananas" and "BANANAS".

const UNITS = [
  'lb', 'lbs', 'pound', 'pounds', 'oz', 'ounce', 'ounces', 'kg', 'kgs', 'kilo', 'kilos', 'g', 'gram', 'grams',
  'l', 'liter', 'liters', 'litre', 'litres', 'ml', 'gal', 'gallon', 'gallons', 'qt', 'quart', 'quarts', 'pt', 'pint', 'pints',
  'dozen', 'doz', 'pack', 'packs', 'pk', 'package', 'packages', 'pkg', 'can', 'cans', 'jar', 'jars', 'bottle', 'bottles',
  'box', 'boxes', 'bag', 'bags', 'bunch', 'bunches', 'head', 'heads', 'loaf', 'loaves', 'carton', 'cartons',
  'container', 'containers', 'tub', 'tubs', 'case', 'cases', 'roll', 'rolls', 'stick', 'sticks', 'ct', 'count', 'each', 'ea',
  'cup', 'cups', 'block', 'blocks', 'piece', 'pieces', 'pcs', 'tray', 'trays', 'sleeve', 'sleeves',
];
const UNIT_RE = UNITS.map((u) => u.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')).sort((a, b) => b.length - a.length).join('|');
const NUM = String.raw`(?:\d+\s+\d+\s*/\s*\d+|\d+\s*/\s*\d+|\d+(?:[.,]\d+)?(?:\s*-\s*\d+)?|[½¼¾⅓⅔]|(?:a\s+couple(?:\s+of)?|a\s+few|half(?:\s+a)?|an|a|one|two|three|four|five|six|seven|eight|nine|ten|twelve)\b)`;

// "2 lbs of", "1/2 gallon", "3x", "a dozen", "two cans of" — at the start
const LEAD_QTY = new RegExp(String.raw`^(${NUM})\s*(?:(?:x|×)(?=\s)|(${UNIT_RE})\.?\b)?\s*(?:of\s+)?`, 'i');
// "dozen eggs", "bag of ice" without a number
const LEAD_UNIT = new RegExp(String.raw`^(dozen|${UNIT_RE})\s+of\s+|^(dozen)\s+`, 'i');
// "milk x2", "milk ×2", "milk (2)", "milk - 2", "eggs, 2 dozen"
const TRAIL_QTY = new RegExp(String.raw`(?:\s+[x×]\s*(\d+)|\s*\((\d+(?:\s*(?:${UNIT_RE}))?)\)|\s+-\s+(\d+(?:\s*(?:${UNIT_RE}))?)|,\s*(\d+(?:\s*(?:${UNIT_RE}))?))\s*$`, 'i');

export interface ParsedEntry {
  name: string;
  quantity: string;
}

/** Splits "2 lbs ground beef" into { name: "ground beef", quantity: "2 lbs" }. */
export function parseEntry(input: string): ParsedEntry {
  let text = input.replace(/\s+/g, ' ').trim();
  let quantity = '';
  const trail = text.match(TRAIL_QTY);
  if (trail && trail.index! > 0) {
    const q = trail[1] ? `x${trail[1]}` : (trail[2] ?? trail[3] ?? trail[4] ?? '');
    quantity = q.trim();
    text = text.slice(0, trail.index).trim();
  }
  if (!quantity) {
    const lead = text.match(LEAD_QTY);
    // a number followed by % or a letter is part of the name: "2% milk", "7up", "a1 sauce"
    const rest = lead ? text.slice(lead[0].length) : '';
    const partOfName = lead && (/^%/.test(rest) || (/^\d+[a-z%]/i.test(text) && !new RegExp(String.raw`^\d+(?:[x×]|${UNIT_RE})\b`, 'i').test(text)) || (/^(a|an|one)$/i.test(lead[1]) && !lead[2] && rest.split(' ').length > 3));
    if (lead && lead[0].trim() && rest && !partOfName && !(lead[1] && !lead[2] && /^[a-z]$/i.test(lead[1]))) {
      const isArticle = /^(a|an)$/i.test(lead[1]);
      quantity = isArticle && !lead[2] ? '' : lead[0].replace(/\s+of\s*$/i, '').trim();
      text = rest.trim();
    } else {
      const unit = text.match(LEAD_UNIT);
      if (unit && text.length > unit[0].length) {
        quantity = (unit[1] ?? unit[2]).trim();
        text = text.slice(unit[0].length).trim();
      }
    }
  }
  quantity = quantity.replace(/^(\d+)\s*[x×]$/i, 'x$1');
  return { name: text.replace(/^[,.;:\-\s]+|[,.;:\-\s]+$/g, ''), quantity };
}

const IRREGULAR: Record<string, string> = {
  loaves: 'loaf', leaves: 'leaf', halves: 'half', knives: 'knife', geese: 'goose', mice: 'mouse', teeth: 'tooth',
  feet: 'foot', children: 'child', wolves: 'wolf', calves: 'calf', shelves: 'shelf', dice: 'dice', people: 'person',
};
const IE_WORDS = new Set(['cookie', 'pie', 'brownie', 'veggie', 'smoothie', 'hoagie', 'pierogie', 'potpie', 'beanie', 'tie', 'nightie', 'rotisserie', 'sweetie', 'calorie', 'goalie', 'zombie', 'lassie', 'birdie', 'pinkie', 'bootie', 'onesie', 'scrunchie', 'selfie', 'freebie', 'movie']);
const KEEP = new Set(['molasses', 'hummus', 'asparagus', 'couscous', 'swiss', 'citrus', 'octopus', 'series', 'species', 'grits', 'news', 'lens', 'gas', 'bus', 'plus', 'cactus', 'hibiscus', 'gras', 'hollandaise', 'mayonnaise', 'lettuce', 'quinoa', 'chips', 'oats', 'jeans', 'pants', 'shorts', 'tongs', 'scissors', 'glasses', 'pajamas', 'leggings', 'tights', 'clothes', 'brussels', 'cheerios', 'wheaties', 'skittles', 'pringles', 'tums', 'sprinkles', 'suds', 'kisses', 'ramen', 'quiches']);

/** "berries" → "berry", "tomatoes" → "tomato". Deterministic: the same word always gives the same result. */
export function singularize(word: string): string {
  const w = word.toLowerCase();
  if (IRREGULAR[w]) return IRREGULAR[w];
  if (KEEP.has(w) || w.length <= 3 || /\d/.test(w)) return w;
  if (/(ss|us|is|ous)$/.test(w)) return w;
  if (w.endsWith('ies')) {
    const stem = w.slice(0, -3);
    return IE_WORDS.has(`${stem}ie`) ? `${stem}ie` : `${stem}y`;
  }
  if (w.endsWith('sses')) return w.slice(0, -2);
  if (/(ch|sh|x|z)es$/.test(w)) return w.slice(0, -2);
  if (w.endsWith('oes') && w.length > 4) return w.slice(0, -2);
  if (w.endsWith('s') && !w.endsWith("'s")) return w.slice(0, -1);
  return w;
}

/** Lowercase words without punctuation: "Ben & Jerry's Ice-Cream" → ["ben", "&", "jerry's", "ice", "cream"]. */
export function words(text: string): string[] {
  return text
    .toLowerCase()
    .normalize('NFKD').replace(/[̀-ͯ]/g, '')
    .replace(/[’`]/g, "'")
    .replace(/[^a-z0-9&%'\s-]/g, ' ')
    .replace(/(^|\s)'+|'+(\s|$)/g, ' ')
    .replace(/-/g, ' ')
    .split(/\s+/)
    .filter(Boolean);
}

/** The name's identity: lowercase, no quantity, no punctuation, each word singular. "2 Bananas!" → "banana". */
export function itemKey(input: string): string {
  const { name } = parseEntry(input);
  return words(name).map(singularize).join(' ');
}

/** "bananas" → "Bananas" (leaves "iPhone" and "BBQ sauce" alone). */
export function displayName(name: string): string {
  const t = name.replace(/\s+/g, ' ').trim();
  if (!t) return t;
  return /^[a-z]/.test(t) && !/^[a-z]+[A-Z]/.test(t) ? t[0].toUpperCase() + t.slice(1) : t;
}
